// Shop (merchant) node inventory model: offer type, pricing, per-Tier rarity
// weights, and the deterministic seed channel for Step 10c-shop. This module
// is pure — it has NO dependency on `generateItem` (content). The client-side
// `generateShopInventory` (client/src/sim/dungeon-shop.ts) orchestrates the
// item rolls and composes them with the helpers here. See ADR 0026 §2.8 +
// §2.8 Amendment (2026-05-30).
//
// Pricing (ADR 0026 §2.8 Amendment): the originally pinned formula
// `basePrice × tierMultiplier × rarityMultiplier` referenced a basePrice
// table that ADR 0027 never landed, and Item carries no price field. Phase 3
// collapses the three factors into an ilvl-derived form — ilvl already
// encodes tier/difficulty (`difficultyIlvl = baseEnemyLevel = tier × 10`):
//
//   price = round(GOLD_PER_ILVL × item.ilvl × SHOP_RARITY_MULTIPLIER[rarity])
//
// GOLD_PER_ILVL and the multipliers are provisional and recalibrated in
// Phase 4 (§2.8 "暫定、Phase 4 校正").
//
// Phase 3 Step 10c-shop.
/**
 * Gold charged per item level. Provisional value (ADR 0026 §2.8 Amendment):
 * with `difficultyIlvl = tier × 10` a Tier-1 common (ilvl ≈ 10, ×1.0) costs
 * ≈ 80 gold, roughly 0.8 of a Tier-1 boss reward (`REWARD_BASE_GOLD = 100`),
 * scaling up with ilvl and rarity. Recalibrated in Phase 4.
 */
export const GOLD_PER_ILVL = 8;
/**
 * Rarity price multiplier (ADR 0026 §2.8 Amendment). Maps the ADR's
 * Common/Uncommon/Rare/Epic table onto the engine rarity enum:
 * Common→normal, Uncommon→magic, Rare→rare, Epic→unique. `set` mirrors
 * `unique` defensively — the shop never stocks set rarity.
 */
export const SHOP_RARITY_MULTIPLIER = {
    normal: 1.0,
    magic: 1.5,
    rare: 2.5,
    unique: 4.0,
    set: 4.0,
};
/**
 * Tier-banded rarity weights (ADR 0026 §2.8). `tierMax` is inclusive; the
 * open-ended top band uses `Infinity`. Higher Tiers shift weight toward rare
 * and unique (Epic). `forcedRarity='unique'` is honoured by `generateItem`
 * via the unique-item catalogue (`buildUniqueItem`), so the 10+ unique weight
 * is safe to roll directly.
 */
export const SHOP_RARITY_WEIGHTS = [
    { tierMax: 4, weights: { normal: 0.6, magic: 0.3, rare: 0.1, unique: 0.0 } },
    { tierMax: 9, weights: { normal: 0.3, magic: 0.4, rare: 0.25, unique: 0.05 } },
    {
        tierMax: Number.POSITIVE_INFINITY,
        weights: { normal: 0.1, magic: 0.3, rare: 0.4, unique: 0.2 },
    },
];
/** Resolve the rarity weights covering `tier`. */
function weightsForTier(tier) {
    for (const band of SHOP_RARITY_WEIGHTS) {
        if (tier <= band.tierMax)
            return band.weights;
    }
    // SHOP_RARITY_WEIGHTS ends with tierMax = Infinity, so this is unreachable.
    throw new Error(`no SHOP_RARITY_WEIGHTS band covers tier=${tier}`);
}
/**
 * Deterministic shop-roll seed for a merchant node. Constants are distinct
 * from the event / subtype / reward channels so a shop on the same
 * `(mapSeed, coord)` rolls independently; tests pin pairwise distinctness.
 * Keyed on `coord` so the two Floor-6 shop nodes stock different inventory.
 */
export function shopSeed(mapSeed, coord) {
    const [floor, index] = coord;
    return ((mapSeed ^ Math.imul(floor, 0x2545f491) ^ Math.imul(index, 0x9e3779b1)) >>> 0);
}
/** Roll the offer count: base 5 ±1 → {4, 5, 6} (ADR 0026 §2.8). */
export function rollShopOfferCount(rng) {
    return 4 + rng.nextInt(3);
}
/**
 * Weighted rarity roll for one offer. Returns one of normal / magic / rare /
 * unique per the Tier band (never `set`). Weights are not renormalised; any
 * floating-point slack falls through to the last (unique) entry, matching
 * `rollOutcome` in dungeon-events.
 */
export function rollShopRarity(tier, rng) {
    const w = weightsForTier(tier);
    const order = [
        ['normal', w.normal],
        ['magic', w.magic],
        ['rare', w.rare],
        ['unique', w.unique],
    ];
    const roll = rng.nextFloat();
    let acc = 0;
    for (const [rarity, weight] of order) {
        acc += weight;
        if (roll < acc)
            return rarity;
    }
    return 'unique';
}
/**
 * Derived gold price for a shop item (ADR 0026 §2.8 Amendment):
 * `round(GOLD_PER_ILVL × ilvl × SHOP_RARITY_MULTIPLIER[rarity])`.
 */
export function shopItemPrice(item) {
    return Math.round(GOLD_PER_ILVL * item.ilvl * SHOP_RARITY_MULTIPLIER[item.rarity]);
}
//# sourceMappingURL=dungeon-shop.js.map